import { JSX, PureComponent } from "react";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { ResourceType } from "@app/resources";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import { FilterCriteriaModel, FilterCriteria } from "@app/shared/FilterCriteria";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import ResourceService from "@app/resources/resourceService";
import { ProviderSetsRequestType } from "@app/provider-sets/enumerations";
import ProviderSetService from "@app/provider-sets/providerSetService";
import CargoColumnRow from "@app/provider-sets/CargoColumnRow";
import { CargoColumnCreateModel, CargoColumnType } from "@app/provider-sets/cargoColumnTypes";

interface CreatePanelProps {
  createFiltered?: boolean;
  defaultSourceId?: string;
  hasProviderValidations?: boolean;
  loading: boolean;
  handleSave: (name: string, description: string, isPrivate: boolean, filterCriteria: FilterCriteriaModel[], sourceProviderId?: string, filterInvalidRecords?: boolean, cargo?: CargoColumnCreateModel[]) => void;
}

interface CreatePanelState {
  cargoColumns: CargoColumnCreateModel[];
  description: string;
  filterCriteria: FilterCriteriaModel[];
  filterCriteriaColumnNames: string[];
  filterCriteriaHasErrors: boolean;
  filterInvalidRecords: boolean;
  isPrivate: boolean;
  loading: boolean;
  name: string;
  nameIsValid: boolean;
  nameIsValidText: string;
  sourceProviderOptions: JSX.Element[];
  sourceProviderSetId: string;
}

class WrappedCreatePanelContent extends PureComponent<CreatePanelProps & FlyoutContextOutgoingProps, CreatePanelState> {
  private readonly providerSetService = new ProviderSetService();
  private readonly resourceService = new ResourceService();

  private nameValidationInterval: number = null;

  constructor(props: CreatePanelProps & FlyoutContextOutgoingProps) {
    super(props);
    this.state = {
      cargoColumns: [],
      description: "",
      filterCriteria: [],
      filterCriteriaColumnNames: [],
      filterCriteriaHasErrors: false,
      filterInvalidRecords: false,
      isPrivate: false,
      loading: false,
      name: "",
      nameIsValid: true,
      nameIsValidText: "",
      sourceProviderOptions: [],
      sourceProviderSetId: "",
    };
  }

  public async componentDidMount() {
    this.setState({ loading: true });

    let sourceProviderOptions = null;
    if (!!this.props.createFiltered) {
      const sourceProviderSets = await this.providerSetService.getProviderSetsByRequestType(ProviderSetsRequestType.SourceProviders, 0, 0);

      const activeProviderSets = sourceProviderSets.providerSetSimpleList.filter((x) => !!x.lastActivationDate);

      if (activeProviderSets.length > 0) {

        if (!!this.props.defaultSourceId && activeProviderSets.some(x => x.providerSetId.toString() === this.props.defaultSourceId))
          this.setState(
            { sourceProviderSetId: this.props.defaultSourceId },
            () => { this.updateFilterColumnNames(); });
        else
          this.setState(
            { sourceProviderSetId: activeProviderSets[0].providerSetId.toString() },
            () => { this.updateFilterColumnNames(); });

        sourceProviderOptions = activeProviderSets
          .map((providerSet) => {
            return <option key={providerSet.providerSetId} value={providerSet.providerSetId}>{providerSet.name}</option>
          });
      }
    }

    this.setState({
      loading: false,
      sourceProviderOptions: sourceProviderOptions
    });
  }

  public componentWillUnmount() {
    window.clearInterval(this.nameValidationInterval);
  }

  private validateName = async (name: string) => {
    window.clearInterval(this.nameValidationInterval);
    this.nameValidationInterval = window.setTimeout(async () => {
      if (!name) {
        this.setState({
          nameIsValid: false,
          nameIsValidText: ""
        });
        return;
      } else if (await this.resourceService.resourceNameExists([ResourceType.ProviderSet, ResourceType.FilteredProviderSet], name)) {
        this.setState({
          nameIsValid: false,
          nameIsValidText: "This name has already been used."
        });
      } else {
        this.setState({
          nameIsValid: true,
          nameIsValidText: ""
        });
      }
    }, 500);
  }

  private async updateFilterColumnNames() {
    if (!this.state.sourceProviderSetId)
      return;

    this.setState({ loading: true });

    this.setState({
      filterCriteriaColumnNames: await this.providerSetService.getFilterProviderSetColumnNames(this.state.sourceProviderSetId.toString()),
      loading: false
    });
  }

  public render() {
    return (
      <>
        {this.props.loading || this.state.loading ? <LoadingSpinner /> :
          <>
            <div className="col-9">
              {!!this.props.createFiltered ?
                <QESSelect
                  testId="create-provider-set-panel-content__source"
                  containerClassName="mb-3"
                  label="Source Network"
                  value={this.state.sourceProviderSetId}
                  onChange={(e) => {
                    this.setState(
                      { sourceProviderSetId: e.target.value },
                      () => { this.updateFilterColumnNames(); }
                    );
                  }}
                >
                  {this.state.sourceProviderOptions}
                  required={true}
                </QESSelect> : null
              }
              <QESTextInput
                testId={"create-provider-set-panel-content__name"}
                containerClassName="mb-3"
                label="Name"
                value={this.state.name}
                onChange={(e) => {
                  this.setState({ name: e.target.value });
                  this.validateName(e.target.value);
                }}
                isValid={this.state.nameIsValid}
                errorText={this.state.nameIsValidText}
                required={true}
              />
              <QESTextArea
                testId={"create-provider-set-panel-content__description"}
                containerClassName="mb-3"
                label="Description"
                value={this.state.description}
                onChange={(e) => this.setState({ description: e.target.value })}
                isValid={!!this.state.description && !!this.state.description.trim()}
                required={true}
              />
              {!this.props.createFiltered && (
                <>
                  {this.state.cargoColumns.map((col, i) => (
                    <div key={i}>
                      {i > 0 && <hr />}
                      <CargoColumnRow
                        index={i}
                        column={col}
                        showRemove={i === this.state.cargoColumns.length - 1}
                        onChange={(updated) => this.setState((prev) => {
                          const next = [...prev.cargoColumns];
                          next[i] = updated;
                          return { cargoColumns: next };
                        })}
                        onRemove={() => this.setState((prev) => ({
                          cargoColumns: prev.cargoColumns.slice(0, -1)
                        }))}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    data-testid="create-provider-set-panel-content__add-cargo-column"
                    className={`qes-button qes-button-secondary p-0 mb-3${this.state.cargoColumns.length > 0 ? " ms-5" : ""}`}
                    onClick={() => this.setState((prev) => ({
                      cargoColumns: [...prev.cargoColumns, { length: null, name: "", precision: null, scale: null, type: "" }]
                    }))}
                  >
                    + {this.state.cargoColumns.length === 0 ? "Add Cargo Column" : "Add Additional Cargo Column"}
                  </button>
                </>
              )}
              <QESCheckbox
                testId={"create-provider-set-panel-content__private"}
                label="This is a Private Resource (can be administered only by you)"
                checked={this.state.isPrivate}
                onChange={(e) => this.setState({ isPrivate: e.target.checked })}
                containerClassName="mb-3"
              />
              {!!this.state.sourceProviderSetId && this.props.hasProviderValidations ?
               <RoleRestricted allowedRoles={[Roles.PROVIDER_VALIDATION_USER]}>
                  <QESCheckbox
                    testId={"create-provider-set-panel-content__invalidation-filter"}
                    label="Filter out invalid records"
                    checked={this.state.filterInvalidRecords}
                    onChange={(e) => this.setState({ filterInvalidRecords: e.target.checked })}
                    containerClassName="mb-3"
                    />
                </RoleRestricted>
                : null
              }
              {!!this.state.sourceProviderSetId ?
                <FilterCriteria
                  fieldNames={this.state.filterCriteriaColumnNames}
                  onChange={(response) => {
                    this.setState({
                      filterCriteria: response.criteria,
                      filterCriteriaHasErrors: response.hasErrors
                    })
                  }} />
                : null
              }
            </div>
            <FlyoutFooter
              submitButton={<QESButton qesTestId="create-provider-set-panel-content__confirm"
                disabled={
                  !this.state.name ||
                  !this.state.nameIsValid ||
                  !this.state.description ||
                  this.state.filterCriteriaHasErrors ||
                  this.state.cargoColumns.some((c) =>
                    !c.name ||
                    !c.type ||
                    (c.type === CargoColumnType.String && c.length === null) ||
                    (c.type === CargoColumnType.Decimal && (c.scale === null || c.precision === null))
                  )
                }
                onClick={() => {
                  this.props.handleSave(this.state.name, this.state.description, this.state.isPrivate, this.state.filterCriteria, this.state.sourceProviderSetId, this.state.filterInvalidRecords, this.state.cargoColumns);
                  this.props.handleCloseFlyout();
                }}
              >Save</QESButton>
              }
            />
          </>
        }
      </>
    );
  }
}

const CreatePanelContent = withFlyoutContext(WrappedCreatePanelContent);
export default CreatePanelContent;
